import {useContainer} from "#imports";
import {DependencyInjectId} from "@@/package/const/DependencyInjectId";
import LearningMapper from "~~/package/mapper/learning/LearningMapper";
import {LearningCourseModel} from "~~/package/mapper/learning/model/LearningCourseModel";
import {TaskModel} from "~~/package/mapper/learning/model/TaskModel";
import type TaskCompleteRequest from "~~/package/apis/learning/request/TaskCompleteRequest";
import type TaskAnswerSaveRequest from "~~/package/apis/learning/request/TaskAnswerSaveRequest";
import {useLearningStore} from "~/store/learning/learningStore";
import {CodeInputModel} from "~~/package/mapper/learning/model/CodeInputModel";
import {CodeSelectionModel} from "~~/package/mapper/learning/model/CodeSelectionModel";
import {CodeOrderModel} from "~~/package/mapper/learning/model/CodeOrderModel";
import type TaskCompleteResponse from "~~/package/apis/learning/response/TaskCompleteResponse";
import type TaskAnswerResponse from "~~/package/apis/learning/response/TaskAnswerResponse";
import {useUserStore} from "~/store/user/userStore";
import {LearningHistorySubType, LearningHistoryType} from "~~/package/mapper/learning/model/LearningHistoryType";
import {LearningTokenData} from "~~/package/mapper/learning/model/LearningTokenData";
import {TaskType} from "~~/package/mapper/learning/model/TaskType";
import {utf8ToBase64} from "~~/package/utils/Base64Util";

export const useLearningService = () => {
    const learningMapper = useContainer<LearningMapper>(DependencyInjectId.LearningMapper);
    const learningStore = useLearningStore();
    const userStore = useUserStore();

    const getLearningCourse = (id: number, lang: string): Promise<LearningCourseModel> => {
        return learningMapper.getLearningCourse(id, lang);
    };

    const getTask = async (taskId: number, lang: string): Promise<TaskModel> => {
        return await learningMapper.getTask(taskId, lang);
    }

    const getTasksWithLatestCodes = async (taskId: number, lang: string): Promise<TaskModel> => {
        const taskData = getTask(taskId, lang);

        if (([TaskType.CODE, TaskType.MULTI_FILE_CODE, TaskType.CODE_WITHOUT_RUN] as TaskType[]).includes((await taskData).type as TaskType))
            (await taskData).submitCode = await getLatestSavedCode(taskId, "training", learningStore.course.id);
        return taskData
    }

    const getToken = (locale: string, domain?: string, domainId?: number): Promise<LearningTokenData> => {
        return learningMapper.getToken(
            learningStore.course.id,
            learningStore.task.id,
            learningStore.task.langType,
            locale,
            domain,
            domainId
        );
    }

    const requestToSubChapterComplete = (request: TaskCompleteRequest): Promise<TaskCompleteResponse> => {
        return learningMapper.requestToSubChapterComplete(request, learningStore.tokenData);
    }

    const getTaskAnswer = (taskId: number): Promise<TaskAnswerResponse> => {
        return learningMapper.getTaskAnswer(taskId);
    }

    const saveCodeHistory = (submitData?: string): Promise<boolean> => {
        return learningMapper.saveCodeHistory(learningStore.task.id, utf8ToBase64(submitData ?? learningStore.convertSavingCode));
    }

    const saveCode = (submitData?: string): Promise<boolean> => {
        const request: TaskAnswerSaveRequest = {
            id: learningStore.task.id,
            submitCode: utf8ToBase64(submitData ?? learningStore.convertSavingCode),
            dictationAnswerCodeSubmitList: learningStore.task.markDictationList.map((dictation: CodeInputModel) => ({id: dictation.id, userAnswer: utf8ToBase64(dictation.value)})),
            selectionAnswerCodeSubmitList: learningStore.task.markSelectionList.map((selection: CodeSelectionModel) => ({id: selection.id, userAnswer: selection.selectedIndex})),
            orderAnswerCodeSubmitList: learningStore.task.markOrderList.map((order: CodeOrderModel) => ({id: order.id, userAnswer: order.answerIndex}))
        };
        return learningMapper.saveCode(request);
    }

    const saveUserCode = (domainType: string, domainId: number, code: string): Promise<boolean> => {
        return learningMapper.saveUserCode(learningStore.task.id, domainType, domainId, utf8ToBase64(code));
    }
    
    const getLatestSavedCode = (taskId: number, domainType: string, domainId: number): Promise<string> => {
        return learningMapper.getLatestSavedCode(taskId, domainType, domainId);
    }

    const getTaskForValidate = (uuid: string): Promise<TaskModel> => {
        return learningMapper.getTaskForValidate(uuid);
    }

    const getTaskValidity = (uuid: string, request: TaskCompleteRequest): Promise<TaskCompleteResponse> => {
        return learningMapper.getTaskValidity(uuid, request, learningStore.tokenData);
    }

    const saveTaskDuration = (amountInSec: number, domainType: string, domainId: number, taskId: number): Promise<boolean> => {
        return learningMapper.saveTaskDuration(taskId, domainType, domainId, amountInSec);
    }

    return {
        getLearningCourse,
        getTask,
        getTasksWithLatestCodes,
        getToken,
        requestToSubChapterComplete,
        getTaskAnswer,
        saveCodeHistory,
        saveCode,
        saveUserCode,
        getLatestSavedCode,
        getTaskForValidate,
        getTaskValidity,
        saveTaskDuration
    };
}
